// DealDetailView.js - Enhanced Stage Slider Version
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Grid,
  Avatar,
  Paper,
  Divider,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DISPOSITION_OPTIONS, SUB_DISPOSITION_OPTIONS, REASON_FOR_CONTACT_OPTIONS, COMPLAINT_TYPE_OPTIONS, MACHINE_NUMBER_OPTIONS, ERROR_CODE_OPTIONS } from "../../utils/constants";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TimelineForm from "../Forms/TimelineForm";
import NotesForm from "../Forms/NotesForm";
import FilesForm from "../Forms/FilesForm";
import { DealDrawer } from "../DealDrawer";
import { dealsAPI } from "../../services/api";
import { useLeadData } from "../../hooks/useLeadData";
import { useNavigate, useParams } from "react-router-dom";
import StageHistoryTable from "../Views/StageHistoryTable";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";
import { EditIcon, Phone, PhoneCall, Mail, Globe, Link as Link2, ArrowBigLeft, UserPen, User, ChevronLeft, ChevronRight, UserCheck, ClipboardList, Wrench, AlertCircle, FileText } from "lucide-react";
import ActivityTable from "../Views/ActivityTable";

const COMPLAINT_STATUS_OPTIONS = [
  "Credit note promised",
  "Refund request",
  "Cx Received juice",
  "Credit note processed",
  "Refund processed",
  "Resolved"
];

const COMMAND_CODE_OPTIONS = [
  "500",
  "1000"
];

// ===== Dynamic Category Hook =====
const useDynamicCategories = () => {
  const [categories, setCategories] = useState([]);
  const userId = localStorage.getItem("userId");

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.getCategories}/${userId}`);
      if (res.data.success) setCategories(res.data.data);
    } catch (err) { console.error("Error fetching categories:", err); }
  };

  const createCategory = async (name) => {
    try {
      const res = await axiosInstance.post(apiEndPointsConfig.createCategory, { name, userId });
      if (res.data.success) { setCategories(prev => [...prev, res.data.data].sort((a, b) => a.name.localeCompare(b.name))); return res.data.data; }
    } catch (err) { console.error("Error creating category:", err); }
    return null;
  };

  const addSubCategory = async (categoryId, subName) => {
    try {
      const res = await axiosInstance.put(`${apiEndPointsConfig.addSubCategory}/${categoryId}/sub-category`, { name: subName });
      if (res.data.success) { setCategories(prev => prev.map(c => c._id === categoryId ? res.data.data : c)); return res.data.data; }
    } catch (err) { console.error("Error adding sub-category:", err); }
    return null;
  };

  useEffect(() => { fetchCategories(); }, []);

  return { categories, fetchCategories, createCategory, addSubCategory };
};

// ===== Dynamic Options Hook (Age/City) =====
const useDynamicOptions = () => {
  const [options, setOptions] = useState({ age: [], city: [] });
  const userId = localStorage.getItem("userId");

  const fetchOptions = async (type) => {
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.getDynamicOptions}/${userId}?type=${type}`);
      if (res.data.success) {
        setOptions(prev => ({ ...prev, [type]: res.data.data }));
      }
    } catch (err) { console.error(`Error fetching dynamic options (${type}):`, err); }
  };

  const createOption = async (name, type) => {
    try {
      const res = await axiosInstance.post(apiEndPointsConfig.createDynamicOption, { name, type, userId });
      if (res.data.success) {
        setOptions(prev => ({
          ...prev,
          [type]: [...prev[type], res.data.data].sort((a, b) => a.name.localeCompare(b.name))
        }));
        return res.data.data;
      }
    } catch (err) { console.error(`Error creating dynamic option (${type}):`, err); }
    return null;
  };

  useEffect(() => {
    fetchOptions("age");
    fetchOptions("city");
  }, []);

  return { options, fetchOptions, createOption };
};

// ==================== HELPER FUNCTIONS ====================

const getInitials = (firstName, lastName) => {
  return `${(firstName || "").charAt(0)}${(lastName || "").charAt(
    0
  )}`.toUpperCase();
};

const getCompanyInitials = (name) => {
  if (!name) return "?";
  const nameStr = typeof name === "string" ? name : name?.name || "";
  if (!nameStr) return "?";
  const words = nameStr.split(" ");
  return words
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDetailedDate = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// ==================== TAB COMPONENTS ====================

const TimelineTab = ({ deal }) => (
  <Box sx={{ p: 0, textAlign: "center", color: "#6b7280" }}>
    <TimelineForm dealId={deal?._id} />
  </Box>
);

const NotesTab = ({ deal }) => (
  <Box sx={{ p: 0 }}>
    <NotesForm dealId={deal?._id} />
  </Box>
);

const ActivitiesTab = ({ deal }) => (
  <Box sx={{ p: 0 }}>
    <ActivityTable />
  </Box>
);

const EmailsTab = ({ deal }) => (
  <Box sx={{ p: 3 }}>
    <Typography variant="body2" sx={{ color: "#6b7280" }}>
      No emails found
    </Typography>
  </Box>
);

const StageHistoryTab = ({ deal }) => (
  <Box sx={{ p: 0 }}>
    <StageHistoryTable />
  </Box>
);

const FilesTab = ({ deal }) => (
  <Box sx={{ p: 0 }}>
    <FilesForm dealId={deal?._id} />
  </Box>
);

const POCTab = ({ deal, onUpdate }) => {
  const isViewer = localStorage.getItem("userRole") === "viewer";
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    pocIdentified: deal.pocIdentified || "",
    pocName: deal.pocName || "",
    pocPhone: deal.pocPhone || "",
    pocEmail: deal.pocEmail || "",
    pocComments: deal.pocComments || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const success = await onUpdate(formData);
      if (success) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating POC details:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      pocIdentified: deal.pocIdentified || "",
      pocName: deal.pocName || "",
      pocPhone: deal.pocPhone || "",
      pocEmail: deal.pocEmail || "",
      pocComments: deal.pocComments || "",
    });
    setIsEditing(false);
  };

  return (
    <Box sx={{ p: 0 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, bgcolor: '#eff6ff', borderRadius: 1.5, color: '#2563eb' }}>
              <UserCheck size={24} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
                Point of Contact Details
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Details of the identified POC for this lead
              </Typography>
            </Box>
          </Box>
          {!isEditing ? (
            !isViewer && (
              <Button
                startIcon={<EditIcon size={16} />}
                onClick={() => setIsEditing(true)}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Edit POC
              </Button>
            )
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#64748b', borderColor: '#e2e8f0' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                size="small"
                disabled={isSubmitting}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
              >
                {isSubmitting ? "Saving..." : "Save Details"}
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: isEditing ? '#fff' : '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                POC Identified?
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    name="pocIdentified"
                    value={formData.pocIdentified}
                    onChange={handleChange}
                    sx={{ borderRadius: 1.5 }}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 600, color: deal.pocIdentified === 'yes' ? '#16a34a' : '#ef4444', textTransform: 'capitalize' }}>
                  {deal.pocIdentified || "—"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: isEditing ? '#fff' : '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                POC Name
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="pocName"
                  value={formData.pocName}
                  onChange={handleChange}
                  size="small"
                  sx={{ "& .MuiInputBase-root": { borderRadius: 1.5 } }}
                />
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  {deal.pocName || "—"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: isEditing ? '#fff' : '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                POC Phone Number
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="pocPhone"
                  value={formData.pocPhone}
                  onChange={handleChange}
                  size="small"
                  sx={{ "& .MuiInputBase-root": { borderRadius: 1.5 } }}
                />
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  {deal.pocPhone || "—"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: isEditing ? '#fff' : '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                POC Email
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="pocEmail"
                  value={formData.pocEmail}
                  onChange={handleChange}
                  size="small"
                  sx={{ "& .MuiInputBase-root": { borderRadius: 1.5 } }}
                />
              ) : (
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  {deal.pocEmail || "—"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: isEditing ? '#fff' : '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                POC Comments
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="pocComments"
                  value={formData.pocComments}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ "& .MuiInputBase-root": { borderRadius: 1.5 } }}
                />
              ) : (
                <Typography variant="body1" sx={{ color: '#475569', whiteSpace: 'pre-wrap' }}>
                  {deal.pocComments || "—"}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const ComplaintTab = ({ deal, onUpdate }) => {
  const isViewer = localStorage.getItem("userRole") === "viewer";
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    complaintDate: deal.complaintDate || "",
    complaintTime: deal.complaintTime || "",
    loggedBy: deal.loggedBy || "",
    callerName: deal.callerName || "",
    paymentType: deal.paymentType || "",
    machineNumber: deal.machineNumber || "",
    reasonForContact: deal.reasonForContact || "",
    complaintType: deal.complaintType || "",
    errorCode: deal.errorCode || "",
    juicesPushed: deal.juicesPushed || "",
    creditNoteJuices: deal.creditNoteJuices || "",
    commandCode: deal.commandCode || "",
    complaintStatus: deal.complaintStatus || "",
    refundAmount: deal.refundAmount || "",
    customerEmailAddress: deal.customerEmailAddress || "",
    complaintComments: deal.complaintComments || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const success = await onUpdate(formData);
      if (success) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating complaint details:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      complaintDate: deal.complaintDate || "",
      complaintTime: deal.complaintTime || "",
      loggedBy: deal.loggedBy || "",
      callerName: deal.callerName || "",
      paymentType: deal.paymentType || "",
      machineNumber: deal.machineNumber || "",
      reasonForContact: deal.reasonForContact || "",
      complaintType: deal.complaintType || "",
      errorCode: deal.errorCode || "",
      juicesPushed: deal.juicesPushed || "",
      creditNoteJuices: deal.creditNoteJuices || "",
      commandCode: deal.commandCode || "",
      complaintStatus: deal.complaintStatus || "",
      refundAmount: deal.refundAmount || "",
      customerEmailAddress: deal.customerEmailAddress || "",
      complaintComments: deal.complaintComments || "",
    });
    setIsEditing(false);
  };

  const displayField = (label, value, icon) => (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
      <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        {icon} {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, color: value ? '#0f172a' : '#cbd5e1' }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, bgcolor: '#fee2e2', borderRadius: 1.5, color: '#ef4444' }}>
              <AlertCircle size={24} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
                Complaint & Ticket Details
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Customer complaint registers and machine diagnostic logs
              </Typography>
            </Box>
          </Box>
          {!isEditing ? (
            !isViewer && (
              <Button
                startIcon={<EditIcon size={16} />}
                onClick={() => setIsEditing(true)}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#6366f1', borderColor: '#6366f1' }}
              >
                Edit Details
              </Button>
            )
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#64748b', borderColor: '#e2e8f0' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                size="small"
                disabled={isSubmitting}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>

          {/* Row 2: Logged By & Caller Name */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Logged By"
                name="loggedBy" value={formData.loggedBy} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Logged By", formData.loggedBy)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Caller Name"
                name="callerName" value={formData.callerName} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Caller Name", formData.callerName)
            )}
          </Grid>

          {/* Row 3: Machine Number & Payment Type */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={MACHINE_NUMBER_OPTIONS}
                value={formData.machineNumber || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, machineNumber: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Machine #"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              displayField("Machine #", formData.machineNumber, <Wrench size={12} />)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <FormControl fullWidth size="small">
                <InputLabel>Payment Type</InputLabel>
                <Select
                  name="paymentType" value={formData.paymentType} onChange={handleChange}
                  label="Payment Type" sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Debit Card">Debit Card</MenuItem>
                  <MenuItem value="Credit Card">Credit Card</MenuItem>
                  <MenuItem value="Wallet">Wallet</MenuItem>
                </Select>
              </FormControl>
            ) : (
              displayField("Payment Type", formData.paymentType)
            )}
          </Grid>

          {/* Row 4: Reason For Contact & Complaint Type */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={REASON_FOR_CONTACT_OPTIONS}
                value={formData.reasonForContact || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, reasonForContact: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Reason For Contact"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              displayField("Reason For Contact", formData.reasonForContact)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={COMPLAINT_TYPE_OPTIONS}
                value={formData.complaintType || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, complaintType: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Complaint Type"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              displayField("Complaint Type", formData.complaintType)
            )}
          </Grid>

          {/* Row 5: Error Code & Refund Amount */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={ERROR_CODE_OPTIONS}
                value={formData.errorCode || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, errorCode: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Error Code"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              displayField("Error Code", formData.errorCode)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Refunds (AED)"
                name="refundAmount" value={formData.refundAmount} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Refunds (AED)", formData.refundAmount)
            )}
          </Grid>

          {/* Row 6: Juices Pushed Count & Credit Note Juices Count */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Juices Pushed Count"
                name="juicesPushed" value={formData.juicesPushed} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Juices Pushed Count", formData.juicesPushed)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Credit Note Juices Count"
                name="creditNoteJuices" value={formData.creditNoteJuices} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Credit Note Juices Count", formData.creditNoteJuices)
            )}
          </Grid>

          {/* Row 7: Command Code */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={COMMAND_CODE_OPTIONS}
                value={formData.commandCode || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, commandCode: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Command Code"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              displayField("Command Code", formData.commandCode)
            )}
          </Grid>

          {/* Row 7: Status & Customer Email Address */}
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Autocomplete
                fullWidth
                size="small"
                options={COMPLAINT_STATUS_OPTIONS}
                value={formData.complaintStatus || ""}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, complaintStatus: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Complaint Status"
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            ) : (
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Status
                </Typography>
                {formData.complaintStatus ? (
                  <Chip
                    label={formData.complaintStatus}
                    size="small"
                    sx={{
                      bgcolor: formData.complaintStatus.toLowerCase().includes("credit") ? "#dcfce7" : "#f1f5f9",
                      color: formData.complaintStatus.toLowerCase().includes("credit") ? "#166534" : "#475569",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                    }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#cbd5e1' }}>—</Typography>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Customer Email"
                name="customerEmailAddress" value={formData.customerEmailAddress} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              displayField("Customer Email", formData.customerEmailAddress)
            )}
          </Grid>

          {/* Row 8: Comments */}
          <Grid item xs={12}>
            {isEditing ? (
              <TextField
                fullWidth size="small" label="Comments" multiline rows={3}
                name="complaintComments" value={formData.complaintComments} onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
              />
            ) : (
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Comments
                </Typography>
                <Typography variant="body1" sx={{ color: formData.complaintComments ? '#475569' : '#cbd5e1', whiteSpace: 'pre-wrap' }}>
                  {formData.complaintComments || "—"}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const ComplaintDetailsTab = ({ deal, onUpdate }) => (
  <Box sx={{ p: 0 }}>
    <ComplaintTab deal={deal} onUpdate={onUpdate} />
  </Box>
);

const FormDataTab = ({ deal }) => (
  <Box sx={{ p: 0 }}>
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ p: 1, bgcolor: '#f0f9ff', borderRadius: 1.5, color: '#0ea5e9' }}>
          <ClipboardList size={24} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
            Form Submission Data
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Additional fields captured from the source form
          </Typography>
        </Box>
      </Box>

      {!deal.field_data || deal.field_data.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #e2e8f0' }}>
          <Typography variant="body2" sx={{ color: "#64748b", fontStyle: 'italic' }}>
            No additional form data available for this lead.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {deal.field_data.map((field, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #f1f5f9', height: '100%' }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '0.65rem', display: 'block', mb: 0.5, lineHeight: 1.2 }}>
                  {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>
                  {field.values.join(', ') || "—"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  </Box>
);

const ProductsTab = ({ deal }) => (
  <Box sx={{ width: "100%", overflowX: "auto" }}>
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        minWidth: 600,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Products
        </Typography>

        <Button
          variant="outlined"
          sx={{
            borderRadius: 999,
            textTransform: "none",
            px: 2.5,
            fontWeight: 500,
            borderColor: "#16a34a",
            color: "#16a34a",
            fontSize: { xs: "0.75rem", md: "0.875rem" },
          }}
        >
          + Product
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "3fr 1.5fr 1fr 1fr 1.5fr",
          px: 2.5,
          py: 1,
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "#f9fafb",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Product
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          List Price (AED)
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Quantity
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Discount
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Total (AED)
        </Typography>
      </Box>

      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "#6b7280",
          fontSize: 14,
        }}
      >
        This record doesn&apos;t have any associated Product.
      </Box>
    </Paper>
  </Box>
);

// Tab configuration
const getTabConfig = (deal, userId) => {
  const config = [
    { label: "Timeline", value: 0, component: TimelineTab, badge: null },
    { label: "Notes", value: 1, component: NotesTab, badge: "4" },
    { label: "POC Information", value: 2, component: POCTab, badge: null },
  ];

  if (userId === "69afdc065323c68fd76cdabf") {
    config.push({ label: "Complaint Details", value: 3, component: ComplaintDetailsTab, badge: null });
  }

  config.push(
    { label: "Activities", value: 4, component: ActivitiesTab, badge: null },
    { label: "Stage History", value: 5, component: StageHistoryTab, badge: null },
    { label: "Files", value: 6, component: FilesTab, badge: null },
    { label: "More Details", value: 7, component: FormDataTab, badge: deal?.field_data?.length || null }
  );

  return config;
};

const DealDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stageChangeDialog, setStageChangeDialog] = useState({
    open: false,
    selectedStage: null,
  });
  const [assigneeChangeDialog, setAssigneeChangeDialog] = useState({
    open: false,
    newMember: null,
  });
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  // Viewers can look at a lead's details but can't move/edit/delete it
  const isViewer = userRole === "viewer";
  const [teamMemberData, setTeamMemberData] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamMemberDetails, setTeamMemberDetails] = useState(null);
  console.log(teamMemberDetails, "teamMemberDetails");

  // Drawer & Toast states
  const [showDrawer, setShowDrawer] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [editQualification, setEditQualification] = useState(false);
  const [qualificationForm, setQualificationForm] = useState({});

  // Pipeline Pagination State
  const STAGES_PER_PAGE = 6;
  const [pipelinePage, setPipelinePage] = useState(0);

  // Get data from custom hook
  const { stages, companies, contacts, notes, dealOperations, noteOperations } =
    useLeadData();

  // Dynamic categories
  const { categories, createCategory, addSubCategory } = useDynamicCategories();
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newSubCategoryName, setNewSubCategoryName] = useState("");
  const [showNewSubCategoryInput, setShowNewSubCategoryInput] = useState(false);

  // Dynamic Options (Age/City)
  const { options, createOption: createDynamicOption } = useDynamicOptions();
  const [newAgeValue, setNewAgeValue] = useState("");
  const [showNewAgeInput, setShowNewAgeInput] = useState(false);
  const [newCityValue, setNewCityValue] = useState("");
  const [showNewCityInput, setShowNewCityInput] = useState(false);

  useEffect(() => {
    getAllDetails(id);
  }, [id]);

  useEffect(() => {
    getMembersDetails();
  }, []);

  const getSingleTeamMember = async (id) => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getSingleTeamMember}/${id}`
      );
      setTeamMemberDetails(res?.data?.data);
    } catch (err) {
      console.log(err);
      setTeamMemberDetails(null);
    }
  };

  const assignMemberToDeal = async (member) => {
    console.log(member, "member");
    try {
      const token = localStorage.getItem("token");

      // Determine old assignee name for timeline
      const oldAssignee = teamMemberData.find((tm) => tm._id === deal.assignee);
      const oldAssigneeName = oldAssignee?.name || "Unassigned";
      const newAssigneeName = member?.name || (member?.firstName ? `${member.firstName} ${member.lastName}` : "Unknown");

      const res = await axiosInstance.put(
        `${apiEndPointsConfig.assignUserToDeal}/${id}/assign`,
        {
          assignId: member?._id,
          previousAssigneeName: oldAssigneeName,
          newAssigneeName: newAssigneeName,
          changedByUserId: userId,
        }
      );

      if (res.data.success) {
        // Update local state immediately
        setDeal((prevDeal) => ({
          ...prevDeal,
          assignee: member?._id,
        }));

        setToast({
          message: `Lead assigned to ${newAssigneeName} successfully!`,
          type: "success",
        });

        // Optionally refresh to ensure DB and UI are in sync
        getAllDetails(id);
      }
    } catch (err) {
      console.error("Error assigning member:", err);
      setToast({
        message: err.response?.data?.message || "Failed to assign team member",
        type: "error",
      });
    }
  };

  const getMembersDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`
      );
      // Filter to only show account managers for assignment
      const managers = (res.data.data || []).filter(m => m.role === "account manager");
      setTeamMemberData(managers);
    } catch (err) {
      console.log(err);
    }
  };

  const addDealTimeline = async (payload) => {
    try {
      await axiosInstance.post(apiEndPointsConfig.createTimeline, payload);
    } catch (error) {
      console.error("Error creating deal timeline:", error);
    }
  };

  const getAllDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const res = await dealsAPI.getSingleLead(id);

      if (res && res.data) {
        setDeal(res.data);
        getSingleTeamMember(res?.data?.assignee);
      } else {
        setError("Failed to load deal details");
      }
    } catch (err) {
      console.error("Error fetching deal:", err);
      setError(err.message || "Failed to load deal");
    } finally {
      setLoading(false);
    }
  };

  // Handle stage change
  const handleStageChange = async (newStageId) => {
    if (isViewer) {
      setToast({ message: "Viewers cannot move leads.", type: "error" });
      setStageChangeDialog({ open: false, selectedStage: null });
      return;
    }
    try {
      const payload = {
        dealName: deal?.dealName ?? "",
        company: deal?.company?._id ?? "",
        contact: deal?.contact?._id ?? "",
        stage: newStageId,
        amount: deal?.amount ?? 0,
        closingDate: deal?.closingDate ?? "",
        priority: deal?.priority ?? "",
        source: deal?.source ?? "",
        description: deal?.description ?? "",
      };

      const result = await dealOperations.update(deal._id, payload);

      if (result.success) {
        setDeal(result.deal);
        setToast({
          message: "Stage updated successfully!",
          type: "success",
        });
        const timelinePayload = {
          dealId: id,
          title: "Stage changed",
          description: result.deal.stageName,
          userId,
          changeTime: new Date(),
        };

        addDealTimeline(timelinePayload);
        getAllDetails(id);
        setStageChangeDialog({ open: false, selectedStage: null });
      } else {
        setToast({
          message: "Failed to update stage!",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error updating stage:", err);
      setToast({
        message: "Error updating stage",
        type: "error",
      });
    }
  };

  // Handle update from drawer
  const handleUpdateDeal = async (values, { setSubmitting = () => { } } = {}) => {
    if (isViewer) {
      setToast({ message: "Viewers cannot edit leads.", type: "error" });
      return { success: false };
    }
    try {
      // Try to resolve new selections from dropdowns
      const selectedCompany = companies.find((c) => c.name === values.company);

      const selectedContact = contacts.find(
        (c) => `${c.firstName} ${c.lastName}` === values.contactName
      );

      const selectedStage = stages.find(
        (s) => s._id === values.stage || s.id === values.stage
      );

      // Fallbacks from existing deal (already has IDs or objects)
      const existingCompanyId =
        typeof deal.company === "object"
          ? deal.company?._id || deal.company?.id
          : deal.company || "";

      const existingContactId =
        typeof deal.contact === "object"
          ? deal.contact?._id || deal.contact?.id
          : deal.contact || "";

      const existingStageId =
        typeof deal.stage === "object"
          ? deal.stage?._id || deal.stage?.id
          : deal.stage || "";

      // Final IDs to send – prefer newly selected, otherwise existing, otherwise ""
      const companyId = selectedCompany?._id || existingCompanyId || "";
      const contactId = selectedContact?._id || existingContactId || "";

      let stageId = "";
      if (values.stage !== undefined) {
        stageId = selectedStage?._id || "";
      } else if (values.complaintStatus !== undefined) {
        if (stages && stages.length > 0) {
          const matchedStage = stages.find(
            (s) => s.name.toLowerCase().trim() === (values.complaintStatus || "").toLowerCase().trim()
          );
          stageId = matchedStage?._id || stages[0]?._id || "";
        } else {
          stageId = existingStageId || "";
        }
      } else {
        stageId = existingStageId || "";
      }

      const payload = {
        dealName: values.dealName !== undefined ? values.dealName : deal.dealName || "",
        company: companyId, // always an id or ""
        companyName: values.company || deal.companyName || "",
        contact: contactId, // always an id or ""
        stage: stageId, // always an id or ""
        amount: values.amount !== undefined ? (values.amount ? Number(values.amount) : "") : deal.amount || "",
        closingDate: values.closingDate !== undefined ? values.closingDate : deal.closingDate || "",
        priority: values.priority !== undefined ? values.priority : deal.priority || "",
        source: values.source !== undefined ? values.source : deal.source || "",
        description: values.description !== undefined ? values.description : deal.description || "",
        // Agent qualification fields
        interest: values.interest !== undefined ? values.interest : deal.interest || "",
        subInterest: values.subInterest !== undefined ? values.subInterest : deal.subInterest || "",
        disposition: values.disposition !== undefined ? values.disposition : deal.disposition || "",
        subDisposition: values.subDisposition !== undefined ? values.subDisposition : deal.subDisposition || "",
        whatsappSent: values.whatsappSent !== undefined ? values.whatsappSent : deal.whatsappSent || false,
        remarks: values.remarks !== undefined ? values.remarks : deal.remarks || "",
        followUpDate: values.followUpDate !== undefined ? values.followUpDate : deal.followUpDate || null,
        city: values.city !== undefined ? values.city : deal.city || "",
        // POC Fields
        pocIdentified: values.pocIdentified !== undefined ? values.pocIdentified : deal.pocIdentified || "",
        pocName: values.pocName !== undefined ? values.pocName : deal.pocName || "",
        pocPhone: values.pocPhone !== undefined ? values.pocPhone : deal.pocPhone || "",
        pocEmail: values.pocEmail !== undefined ? values.pocEmail : deal.pocEmail || "",
        pocComments: values.pocComments !== undefined ? values.pocComments : deal.pocComments || "",
        // Complaint / Ticket Fields
        complaintDate: values.complaintDate !== undefined ? values.complaintDate : deal.complaintDate || "",
        complaintTime: values.complaintTime !== undefined ? values.complaintTime : deal.complaintTime || "",
        loggedBy: values.loggedBy !== undefined ? values.loggedBy : deal.loggedBy || "",
        callerName: values.callerName !== undefined ? values.callerName : deal.callerName || "",
        paymentType: values.paymentType !== undefined ? values.paymentType : deal.paymentType || "",
        machineNumber: values.machineNumber !== undefined ? values.machineNumber : deal.machineNumber || "",
        reasonForContact: values.reasonForContact !== undefined ? values.reasonForContact : deal.reasonForContact || "",
        complaintType: values.complaintType !== undefined ? values.complaintType : deal.complaintType || "",
        errorCode: values.errorCode !== undefined ? values.errorCode : deal.errorCode || "",
        juicesPushed: values.juicesPushed !== undefined ? values.juicesPushed : deal.juicesPushed || "",
        creditNoteJuices: values.creditNoteJuices !== undefined ? values.creditNoteJuices : deal.creditNoteJuices || "",
        commandCode: values.commandCode !== undefined ? values.commandCode : deal.commandCode || "",
        complaintStatus: values.complaintStatus !== undefined ? values.complaintStatus : deal.complaintStatus || "",
        refundAmount: values.refundAmount !== undefined ? values.refundAmount : deal.refundAmount || "",
        customerEmailAddress: values.customerEmailAddress !== undefined ? values.customerEmailAddress : deal.customerEmailAddress || "",
        complaintComments: values.complaintComments !== undefined ? values.complaintComments : deal.complaintComments || "",
        whatsappNumber: values.whatsappNumber !== undefined ? values.whatsappNumber : deal.whatsappNumber || "",
        // UTM parameters
        utm_source: values.utm_source !== undefined ? values.utm_source : deal.utm_source || "",
        utm_medium: values.utm_medium !== undefined ? values.utm_medium : deal.utm_medium || "",
        utm_campaign: values.utm_campaign !== undefined ? values.utm_campaign : deal.utm_campaign || "",
        utm_term: values.utm_term !== undefined ? values.utm_term : deal.utm_term || "",
        utm_content: values.utm_content !== undefined ? values.utm_content : deal.utm_content || "",
      };

      const result = await dealOperations.update(deal._id, payload);

      if (result.success) {
        setDeal(result.deal);
        setToast({
          message: "Deal updated successfully!",
          type: "success",
        });
        getAllDetails(id);
        setShowDrawer(false);
        setSubmitting(false);
        return { success: true };
      } else {
        setToast({
          message: result.error?.response?.data?.message || result.error?.message || "Failed to update deal!",
          type: "error",
        });
        setSubmitting(false);
        return { success: false };
      }
    } catch (err) {
      console.error("Error updating deal:", err);
      setToast({
        message: "Something went wrong while updating the deal.",
        type: "error",
      });
      setSubmitting(false);
      return { success: false };
    }
  };

  // Handle delete from drawer
  const handleDeleteDeal = async () => {
    if (!deal) return;
    if (isViewer) {
      setToast({ message: "Viewers cannot delete leads.", type: "error" });
      return;
    }

    const result = await dealOperations.delete(deal._id);

    if (result.success) {
      setToast({
        message: "Deal deleted successfully!",
        type: "success",
      });
      setShowDrawer(false);
    } else {
      setToast({
        message: "Failed to delete deal!",
        type: "error",
      });
    }
  };

  // Get current stage index
  const currentStageIndex = stages.findIndex(
    (s) => s._id === deal?.stage?._id || s._id === deal?.stage
  );
  const progressPercentage =
    currentStageIndex >= 0
      ? ((currentStageIndex + 1) / stages.length) * 100
      : 0;

  // Auto-set pagination to show current stage
  useEffect(() => {
    if (currentStageIndex >= 0 && stages.length > 0) {
      const page = Math.floor(currentStageIndex / STAGES_PER_PAGE);
      setPipelinePage(page);
    }
  }, [currentStageIndex, stages.length]);

  const TAB_CONFIG = getTabConfig(deal, userId);
  const activeTab = TAB_CONFIG.find((t) => t.value === tab);
  const ActiveComponent = activeTab?.component || ProductsTab;

  // Pagination calculations
  const totalPages = Math.ceil(stages.length / STAGES_PER_PAGE);
  const paginatedStages = stages.slice(
    pipelinePage * STAGES_PER_PAGE,
    (pipelinePage + 1) * STAGES_PER_PAGE
  );

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f5f7fb",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f5f7fb", p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // No data state
  if (!deal) {
    return (
      <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f5f7fb", p: 3 }}>
        <Alert severity="info">No deal found</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2, // More professional 8px/12px radius
          bgcolor: "#ffffff",
          border: "1px solid #e2e8f0", // Subtle border instead of shadow
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        {/* ==================== HEADER ==================== */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "flex-start", lg: "center" },
            justifyContent: "space-between",
            gap: 3,
            mb: 4,
          }}
        >
          {/* Left – Status, Name, Meta */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Chip
                label={deal.status || "Active Lead"}
                size="small"
                sx={{
                  bgcolor: "#f1f5f9",
                  color: "#475569",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderRadius: 1,
                }}
              />
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500 }}>
                ID: {deal._id?.slice(-8).toUpperCase()}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a", // Deep slate
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  letterSpacing: "-0.01em"
                }}
              >
                {deal.dealName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, gap: 3, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 20, height: 20, bgcolor: '#e0e7ff', color: '#6366f1', fontSize: '0.7rem', fontWeight: 700 }}>
                  {getInitials(deal.contact?.firstName, deal.contact?.lastName)}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                  {deal.contact?.firstName && deal.contact?.lastName
                    ? `${deal.contact.firstName} ${deal.contact.lastName}`
                    : deal.mobile}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                  Created At: <Box component="span" sx={{ color: "#0f172a", fontWeight: 600 }}>{deal.createdAt ? formatDetailedDate(deal.createdAt) : "--"}</Box>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right – Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            {(userRole === "admin" || userRole === "owner") && (
              <Box sx={{ minWidth: 220, flexGrow: { xs: 1, sm: 0 } }}>
                <Autocomplete
                  options={teamMemberData}
                  // Better label handling with a fallback
                  getOptionLabel={(option) =>
                    option.name || (option.firstName ? `${option.firstName} ${option.lastName}` : "Unknown Member")
                  }
                  // Finding the value safely
                  value={teamMemberData.find((tm) => tm._id === deal.assignee) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setAssigneeChangeDialog({ open: true, newMember: newValue });
                    }
                  }}

                  // 1. ADD CUSTOM OPTION RENDERING (with Avatars)
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                      <Avatar
                        sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}
                        src={option.profilePic} // Use profile pic if available
                      >
                        {option.firstName?.[0] || option.name?.[0]}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name || `${option.firstName} ${option.lastName}`}
                      </Typography>
                    </Box>
                  )}

                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Assign to..."
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2, // Slightly more rounded
                          bgcolor: "#fcfcfd",
                          transition: "all 0.2s",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            borderColor: "#cbd5e1",
                            bgcolor: "#fff",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)", // Modern focus ring
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none", // Cleaner look with custom border
                        },
                      }}
                      // 2. ADD ICON TO THE START
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <User size={16} style={{
                              marginLeft: "10px"
                            }} color="#64748b" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}


            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>

              <Tooltip title="Back to Lead Management" arrow>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/lead-management-system")}
                  sx={{
                    borderRadius: "50%",
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    px: 0,
                    textTransform: "none",
                    borderColor: "#6366F1",
                    color: "#475569",
                    "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
                    flexShrink: 0,
                  }}
                >
                  <ArrowBigLeft size={20} />
                </Button>
              </Tooltip>

              {!isViewer && (
                <Tooltip title="Edit Lead Details" arrow>
                  <Button
                    variant="outlined"
                    onClick={() => setShowDrawer(true)}
                    sx={{
                      borderRadius: "50%",
                      minWidth: 40,
                      width: 40,
                      height: 40,
                      px: 0,
                      color: "#475569",
                      borderColor: "#6366F1",
                      "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
                      flexShrink: 0,
                    }}
                  >
                    <UserPen size={18} />
                  </Button>
                </Tooltip>
              )}

            </Box>
          </Box>
        </Box>

        {/* ==================== MINIMALIST PIPELINE TRACKER ==================== */}
        <Box sx={{ mb: 4, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Pipeline Status
              </Typography>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPipelinePage(prev => Math.max(0, prev - 1))}
                    disabled={pipelinePage === 0}
                    sx={{ p: 0.5 }}
                  >
                    <ChevronLeft size={16} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setPipelinePage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={pipelinePage === totalPages - 1}
                    sx={{ p: 0.5 }}
                  >
                    <ChevronRight size={16} />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
              {currentStageIndex + 1} of {stages.length} Stages Complete
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: 44,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            {paginatedStages.map((stage, pIdx) => {
              const actualIdx = pipelinePage * STAGES_PER_PAGE + pIdx;
              const isCompleted = actualIdx < currentStageIndex;
              const isCurrent = actualIdx === currentStageIndex;

              return (
                <Box
                  key={stage._id}
                  onClick={() =>
                    !isViewer &&
                    setStageChangeDialog({ open: true, selectedStage: stage })
                  }
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isViewer ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    position: "relative",
                    bgcolor: isCurrent ? "#6366f1" : isCompleted ? "#e0e7ff" : "transparent",
                    color: isCurrent ? "#ffffff" : isCompleted ? "#6366f1" : "#94a3b8",
                    borderRight: pIdx === paginatedStages.length - 1 ? 'none' : '1px solid #e2e8f0',
                    "&:hover": {
                      bgcolor: isCurrent ? "#4f46e5" : "#f1f5f9",
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: isCurrent || isCompleted ? 700 : 500,
                      fontSize: "0.75rem",
                      px: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {isCompleted ? "✓ " : ""}{stage.name}
                  </Typography>

                  {/* Arrow effect for flow */}
                  {pIdx < paginatedStages.length - 1 && !isCurrent && (
                    <Box sx={{
                      position: 'absolute',
                      right: -8,
                      width: 16,
                      height: 16,
                      bgcolor: isCompleted ? "#e0e7ff" : "transparent",
                      transform: 'rotate(45deg)',
                      borderTop: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0',
                      zIndex: 1,
                      display: { xs: 'none', md: 'block' }
                    }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ==================== MAIN CONTENT: LEFT/RIGHT COLUMNS ==================== */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Left column */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Client Interest Card */}
              <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="overline" sx={{ fontWeight: 800, color: '#6366f1', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
                    User Details
                  </Typography>
                  {!isViewer && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setQualificationForm({
                        interest: deal.interest || "",
                        subInterest: deal.subInterest || "",
                        disposition: deal.disposition || "",
                        subDisposition: deal.subDisposition || "",
                        whatsappSent: deal.whatsappSent || false,
                        remarks: deal.remarks || "",
                        age: deal.age || "",
                        city: deal.city || "",
                        followUpDate: deal.followUpDate ? new Date(deal.followUpDate).toISOString().split('T')[0] : "",
                        whatsappNumber: deal.whatsappNumber || "",
                        campaignName: deal.campaignName || "",
                        utm_source: deal.utm_source || "",
                        utm_medium: deal.utm_medium || "",
                        utm_campaign: deal.utm_campaign || "",
                        utm_term: deal.utm_term || "",
                        utm_content: deal.utm_content || "",
                      });
                      setEditQualification(true);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, borderColor: '#6366f1', color: '#6366f1', minWidth: 'auto', px: 1.5, py: 0.3 }}
                  >
                    ✏️ Edit
                  </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Category</Typography>
                    {deal.interest ? (
                      <Chip label={deal.interest} size="small" sx={{
                        bgcolor: deal.interest === "Interested" ? "#DCFCE7" : deal.interest === "Not Interested" ? "#FEE2E2" : "#FEF3C7",
                        color: deal.interest === "Interested" ? "#166534" : deal.interest === "Not Interested" ? "#991B1B" : "#92400E",
                        fontSize: "0.65rem", fontWeight: 700, height: 22,
                      }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Sub Category</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a' }}>{deal.subInterest || "—"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Disposition</Typography>
                    {deal.disposition ? (
                      <Chip label={deal.disposition} size="small" sx={{ bgcolor: "#EEF2FF", color: "#4338CA", fontSize: "0.65rem", fontWeight: 700, height: 22 }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Sub Disposition</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a' }}>{deal.subDisposition || "—"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>WhatsApp</Typography>
                    <Chip label={deal.whatsappSent ? "✅ Sent" : "Not Sent"} size="small" sx={{ bgcolor: deal.whatsappSent ? "#DCFCE7" : "#F3F4F6", color: deal.whatsappSent ? "#166534" : "#6B7280", fontSize: "0.65rem", fontWeight: 700, height: 22 }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Follow Up</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: deal.followUpDate ? '#0f172a' : '#cbd5e1' }}>
                      {deal.followUpDate ? formatDate(deal.followUpDate) : "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Age</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a' }}>{deal.age || "—"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>City</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a' }}>{deal.city || "—"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>WhatsApp Number</Typography>
                    {deal.whatsappNumber ? (
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#16a34a' }}>
                        📱 {deal.whatsappNumber}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Campaign / Product</Typography>
                    {deal.campaignName ? (
                      <Chip label={deal.campaignName} size="small" sx={{ bgcolor: "#EEF2FF", color: "#4338CA", fontSize: "0.65rem", fontWeight: 700, height: 22 }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                    )}
                  </Box>



                  {/* UTM tracking displays */}
                  {(deal.utm_source || deal.utm_medium || deal.utm_campaign || deal.utm_term || deal.utm_content) && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', display: 'block', mb: 1, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>UTM Parameters</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {deal.utm_source && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Source</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{deal.utm_source}</Typography>
                          </Box>
                        )}
                        {deal.utm_medium && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Medium</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{deal.utm_medium}</Typography>
                          </Box>
                        )}
                        {deal.utm_campaign && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Campaign</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{deal.utm_campaign}</Typography>
                          </Box>
                        )}
                        {deal.utm_term && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Term</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{deal.utm_term}</Typography>
                          </Box>
                        )}
                        {deal.utm_content && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Content</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>{deal.utm_content}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {deal.remarks && (
                    <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', mb: 0.5 }}>Remarks</Typography>
                      <Typography variant="caption" sx={{ color: '#475569', lineHeight: 1.5 }}>{deal.remarks}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Description Box */}
              <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', display: 'block', mb: 1, fontSize: '0.65rem', letterSpacing: '0.1em' }}>
                  About Lead
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.8rem' }}>
                  {deal.description || "No description provided."}
                </Typography>
              </Box>



              {/* ===== CLIENT INTEREST MODAL ===== */}
              <Dialog
                open={editQualification}
                onClose={() => setEditQualification(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
              >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', pb: 1 }}>
                  ⚡Edit Lead Details
                  <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 500, mt: 0.5 }}>
                    Update category, disposition, and follow-up details
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <Grid container spacing={2}>
                      {/* Category Dropdown */}
                      <Grid item xs={12} sm={6}>
                        {showNewCategoryInput ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth size="small" label="New Category" autoFocus
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                            />
                            <Button
                              variant="contained" size="small"
                              disabled={!newCategoryName.trim()}
                              onClick={async () => {
                                const created = await createCategory(newCategoryName.trim());
                                if (created) {
                                  setQualificationForm(prev => ({ ...prev, interest: created.name, subInterest: "" }));
                                  setNewCategoryName("");
                                  setShowNewCategoryInput(false);
                                }
                              }}
                              sx={{ borderRadius: '10px', minWidth: 60, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                            >Add</Button>
                            <Button size="small" onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(""); }} sx={{ minWidth: 40, color: '#94a3b8' }}>✕</Button>
                          </Box>
                        ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={qualificationForm.interest || ""}
                              label="Category"
                              onChange={(e) => {
                                if (e.target.value === "__add_new__") { setShowNewCategoryInput(true); return; }
                                setQualificationForm(prev => ({ ...prev, interest: e.target.value, subInterest: "" }));
                              }}
                              sx={{ borderRadius: '10px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              {categories.map(cat => <MenuItem key={cat._id} value={cat.name}>{cat.name}</MenuItem>)}
                              <MenuItem value="__add_new__" sx={{ color: '#6366f1', fontWeight: 700, borderTop: '1px solid #f1f5f9', mt: 0.5 }}>➕ Add New Category</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Grid>

                      {/* Sub Category Dropdown */}
                      <Grid item xs={12} sm={6}>
                        {(() => {
                          const selectedCat = categories.find(c => c.name === qualificationForm.interest);
                          const subOptions = selectedCat ? selectedCat.subCategories : [];
                          return showNewSubCategoryInput ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                fullWidth size="small" label="New Sub Category" autoFocus
                                value={newSubCategoryName}
                                onChange={(e) => setNewSubCategoryName(e.target.value)}
                                sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                              />
                              <Button
                                variant="contained" size="small"
                                disabled={!newSubCategoryName.trim() || !selectedCat}
                                onClick={async () => {
                                  if (selectedCat) {
                                    const updated = await addSubCategory(selectedCat._id, newSubCategoryName.trim());
                                    if (updated) {
                                      setQualificationForm(prev => ({ ...prev, subInterest: newSubCategoryName.trim() }));
                                      setNewSubCategoryName("");
                                      setShowNewSubCategoryInput(false);
                                    }
                                  }
                                }}
                                sx={{ borderRadius: '10px', minWidth: 60, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                              >Add</Button>
                              <Button size="small" onClick={() => { setShowNewSubCategoryInput(false); setNewSubCategoryName(""); }} sx={{ minWidth: 40, color: '#94a3b8' }}>✕</Button>
                            </Box>
                          ) : (
                            <FormControl fullWidth size="small">
                              <InputLabel>Sub Category</InputLabel>
                              <Select
                                value={qualificationForm.subInterest || ""}
                                label="Sub Category"
                                onChange={(e) => {
                                  if (e.target.value === "__add_new__") { setShowNewSubCategoryInput(true); return; }
                                  setQualificationForm(prev => ({ ...prev, subInterest: e.target.value }));
                                }}
                                disabled={!qualificationForm.interest}
                                sx={{ borderRadius: '10px' }}
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {subOptions.map(sub => <MenuItem key={sub} value={sub}>{sub}</MenuItem>)}
                                {qualificationForm.interest && (
                                  <MenuItem value="__add_new__" sx={{ color: '#6366f1', fontWeight: 700, borderTop: '1px solid #f1f5f9', mt: 0.5 }}>➕ Add New Sub Category</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          );
                        })()}
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Disposition</InputLabel>
                          <Select value={qualificationForm.disposition || ""} label="Disposition" onChange={(e) => setQualificationForm(prev => ({ ...prev, disposition: e.target.value }))} sx={{ borderRadius: '10px' }}>
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {DISPOSITION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Sub Disposition</InputLabel>
                          <Select value={qualificationForm.subDisposition || ""} label="Sub Disposition" onChange={(e) => setQualificationForm(prev => ({ ...prev, subDisposition: e.target.value }))} sx={{ borderRadius: '10px' }}>
                            <MenuItem value=""><em>Select</em></MenuItem>
                            {SUB_DISPOSITION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        {showNewAgeInput ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth size="small" label="New Age" autoFocus
                              value={newAgeValue}
                              onChange={(e) => setNewAgeValue(e.target.value)}
                              sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                            />
                            <Button
                              variant="contained" size="small"
                              disabled={!newAgeValue.trim()}
                              onClick={async () => {
                                const created = await createDynamicOption(newAgeValue.trim(), "age");
                                if (created) {
                                  setQualificationForm(prev => ({ ...prev, age: created.name }));
                                  setNewAgeValue("");
                                  setShowNewAgeInput(false);
                                }
                              }}
                              sx={{ borderRadius: '10px', minWidth: 60, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                            >Add</Button>
                            <Button size="small" onClick={() => { setShowNewAgeInput(false); setNewAgeValue(""); }} sx={{ minWidth: 40, color: '#94a3b8' }}>✕</Button>
                          </Box>
                        ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>Age</InputLabel>
                            <Select
                              value={qualificationForm.age || ""}
                              label="Age"
                              onChange={(e) => {
                                if (e.target.value === "__add_new__") { setShowNewAgeInput(true); return; }
                                setQualificationForm(prev => ({ ...prev, age: e.target.value }));
                              }}
                              sx={{ borderRadius: '10px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              {options.age.map(opt => <MenuItem key={opt._id} value={opt.name}>{opt.name}</MenuItem>)}
                              <MenuItem value="__add_new__" sx={{ color: '#6366f1', fontWeight: 700, borderTop: '1px solid #f1f5f9', mt: 0.5 }}>➕ Add New Age</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {showNewCityInput ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth size="small" label="New Location" autoFocus
                              value={newCityValue}
                              onChange={(e) => setNewCityValue(e.target.value)}
                              sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                            />
                            <Button
                              variant="contained" size="small"
                              disabled={!newCityValue.trim()}
                              onClick={async () => {
                                const created = await createDynamicOption(newCityValue.trim(), "city");
                                if (created) {
                                  setQualificationForm(prev => ({ ...prev, city: created.name }));
                                  setNewCityValue("");
                                  setShowNewCityInput(false);
                                }
                              }}
                              sx={{ borderRadius: '10px', minWidth: 60, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                            >Add</Button>
                            <Button size="small" onClick={() => { setShowNewCityInput(false); setNewCityValue(""); }} sx={{ minWidth: 40, color: '#94a3b8' }}>✕</Button>
                          </Box>
                        ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>Location/City</InputLabel>
                            <Select
                              value={qualificationForm.city || ""}
                              label="Location/City"
                              onChange={(e) => {
                                if (e.target.value === "__add_new__") { setShowNewCityInput(true); return; }
                                setQualificationForm(prev => ({ ...prev, city: e.target.value }));
                              }}
                              sx={{ borderRadius: '10px' }}
                            >
                              <MenuItem value=""><em>Select</em></MenuItem>
                              {options.city.map(opt => <MenuItem key={opt._id} value={opt.name}>{opt.name}</MenuItem>)}
                              <MenuItem value="__add_new__" sx={{ color: '#6366f1', fontWeight: 700, borderTop: '1px solid #f1f5f9', mt: 0.5 }}>➕ Add New Location</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Grid>
                    </Grid>



                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small"
                          label="WhatsApp Number"
                          placeholder="e.g. 971501234567"
                          value={qualificationForm.whatsappNumber || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700 }}>📱</Typography>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small"
                          label="Campaign / Product"
                          placeholder="e.g. Kitchen Campaign"
                          value={qualificationForm.campaignName || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, campaignName: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                    </Grid>



                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', mt: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      UTM Parameters
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small"
                          label="UTM Source"
                          value={qualificationForm.utm_source || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, utm_source: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small"
                          label="UTM Medium"
                          value={qualificationForm.utm_medium || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, utm_medium: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth size="small"
                          label="UTM Campaign"
                          value={qualificationForm.utm_campaign || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, utm_campaign: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth size="small"
                          label="UTM Term"
                          value={qualificationForm.utm_term || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, utm_term: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth size="small"
                          label="UTM Content"
                          value={qualificationForm.utm_content || ""}
                          onChange={(e) => setQualificationForm(prev => ({ ...prev, utm_content: e.target.value }))}
                          sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={0} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={<Switch checked={qualificationForm.whatsappSent || false} onChange={(e) => setQualificationForm(prev => ({ ...prev, whatsappSent: e.target.checked }))} color="success" />}
                          label={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>WhatsApp Sent</Typography>}
                          sx={{ m: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Follow Up Date" type="date" value={qualificationForm.followUpDate || ""} onChange={(e) => setQualificationForm(prev => ({ ...prev, followUpDate: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }} />
                      </Grid>
                    </Grid>

                    <TextField fullWidth size="small" label="Remarks" multiline rows={3} value={qualificationForm.remarks || ""} onChange={(e) => setQualificationForm(prev => ({ ...prev, remarks: e.target.value }))} sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }} />
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setEditQualification(false)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#6B7280', borderColor: '#E5E7EB', px: 3 }}
                  >Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        const result = await dealOperations.update(deal._id, qualificationForm);
                        if (result.success) {
                          setDeal(prev => ({ ...prev, ...qualificationForm }));
                          setToast({ message: 'Client interest updated!', type: 'success' });
                          setEditQualification(false);
                          getAllDetails(id);
                        } else {
                          setToast({ message: 'Failed to update', type: 'error' });
                        }
                      } catch (err) {
                        console.error(err);
                        setToast({ message: 'Error saving', type: 'error' });
                      }
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' }, px: 3 }}
                  >Save Changes</Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Grid>

          {/* Right column */}
          <Grid
            item
            xs={12}
            md={8}
            lg={9}
            sx={{
              borderLeft: "1px solid #e9e9e9",
            }}
          >
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "#e2e8f0", mb: 3 }}>
              <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                textColor="primary"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#64748b",
                    px: 3,
                    minWidth: "auto",
                    "&.Mui-selected": {
                      color: "#6366f1",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    height: 2,
                    bgcolor: "#6366f1",
                  },
                }}
              >
                {TAB_CONFIG.map((tabItem) => (
                  <Tab
                    key={tabItem.value}
                    label={tabItem.label}
                    value={tabItem.value}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Dynamic tab content */}
            <Box sx={{ p: { xs: 1, md: 2 } }}>
              <ActiveComponent deal={deal} onUpdate={handleUpdateDeal} />
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* ==================== STAGE CHANGE CONFIRMATION DIALOG ==================== */}
      <Dialog
        open={stageChangeDialog.open}
        onClose={() =>
          setStageChangeDialog({ open: false, selectedStage: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 2, // same padding as actions
            pb: 1, // slightly tighter bottom if you like
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Change Deal Stage?
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2, // match horizontal
            pt: 0.5, // small top spacing under title
            pb: 0, // no extra bottom padding
          }}
        >
          <Typography variant="body2">
            Move deal from <strong>{deal?.stage?.name || "Unknown"}</strong> to{" "}
            <strong>{stageChangeDialog.selectedStage?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
          }}
        >
          <Button
            onClick={() =>
              setStageChangeDialog({ open: false, selectedStage: null })
            }
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              handleStageChange(stageChangeDialog.selectedStage?._id)
            }
            variant="contained"
            sx={{ bgcolor: "#16a34a" }}
          >
            Change Stage
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== ASSIGNEE CHANGE CONFIRMATION DIALOG ==================== */}
      <Dialog
        open={assigneeChangeDialog.open}
        onClose={() =>
          setAssigneeChangeDialog({ open: false, newMember: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ p: 2, pb: 1, fontSize: 16, fontWeight: 600 }}>
          Change Lead Assignee?
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 0.5, pb: 0 }}>
          <Typography variant="body2">
            Are you sure you want to change the assignee from{" "}
            <strong>
              {teamMemberData.find((tm) => tm._id === deal.assignee)?.name ||
                "Unassigned"}
            </strong>{" "}
            to <strong>{assigneeChangeDialog.newMember?.name || (assigneeChangeDialog.newMember?.firstName ? `${assigneeChangeDialog.newMember.firstName} ${assigneeChangeDialog.newMember.lastName}` : "Unknown")}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            onClick={() =>
              setAssigneeChangeDialog({ open: false, newMember: null })
            }
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              assignMemberToDeal(assigneeChangeDialog.newMember);
              setAssigneeChangeDialog({ open: false, newMember: null });
            }}
            variant="contained"
            sx={{ bgcolor: "#6366f1" }}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== DEAL DRAWER ==================== */}
      <DealDrawer
        open={showDrawer}
        deal={deal}
        stages={stages}
        companies={companies}
        contacts={contacts}
        notes={notes.filter((n) => n.dealId === deal._id)}
        onClose={() => setShowDrawer(false)}
        onUpdate={handleUpdateDeal}
        onDelete={handleDeleteDeal}
        onAddNote={noteOperations.add}
        onDeleteNote={noteOperations.delete}
      />

      {/* ==================== TOAST NOTIFICATIONS ==================== */}
      <Snackbar
        open={!!toast.message}
        autoHideDuration={3000}
        onClose={() => setToast({ message: "", type: "success" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ message: "", type: "success" })}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default DealDetailView;
